import type { CSSProperties } from 'react';
import type { AppearanceConfig } from '../../types/configs.generated';
import { getContrastingTextColor } from './colorUtils';

/*
 * JS only owns the runtime accent primitives and a tiny set of approved
 * config-backed semantic overrides.
 * Everything else in `widget-theme.css` stays CSS-owned.
 *
 * Example:
 * - `--rw-accent` drives primary actions directly
 * - `--rw-accent-rgb` exists because CSS needs an `R G B` tuple for alpha recipes like
 *   `rgb(var(--rw-accent-rgb) / 0.05)` and cannot derive that tuple from `--rw-accent`
 * - `--rw-on-accent` is the contrast color for text/icons on accent surfaces
 * - approved semantic overrides such as text roles, CTA-only typography, and radius tokens are
 *   emitted only when `appearance.widget_theme` explicitly overrides them
 * - dynamic-question pills still use the shared widget font family and pill radius from CSS;
 *   CTA-specific overrides here only target conversion CTA buttons
 */
type WidgetThemeVarName =
    | '--rw-accent'
    | '--rw-accent-rgb'
    | '--rw-on-accent'
    | '--rw-glass-effect-alpha-start'
    | '--rw-glass-effect-alpha-end'
    | '--rw-text-primary'
    | '--rw-text-secondary'
    | '--rw-text-placeholder'
    | '--rw-text-disclaimer'
    | '--rw-icon-muted'
    | '--rw-font-family'
    | '--rw-font-family-cta'
    | '--rw-font-weight-regular'
    | '--rw-font-weight-body-strong'
    | '--rw-font-weight-input'
    | '--rw-font-weight-placeholder'
    | '--rw-font-weight-cta-primary'
    | '--rw-font-weight-cta-secondary'
    | '--rw-letter-spacing-body'
    | '--rw-line-height-body'
    | '--rw-letter-spacing-cta'
    | '--rw-line-height-cta'
    | '--rw-radius-shell-compact'
    | '--rw-radius-control-compact'
    | '--rw-radius-card'
    | '--rw-radius-cta';

export type WidgetThemeVars = CSSProperties & Partial<Record<WidgetThemeVarName, string>>;

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
const FONT_FAMILY_PATTERN = /^[^,;{}\n\r]+$/;
const DEFAULT_FONT_FAMILY_FALLBACK =
    "var(--font-dm-sans, 'DM Sans Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif)";

const isHexColor = (value: string | null | undefined): value is string =>
    typeof value === 'string' && HEX_COLOR_PATTERN.test(value);

const normalizePreferredFontFamily = (value: string | null | undefined): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();

    if (!trimmed || !FONT_FAMILY_PATTERN.test(trimmed)) {
        return null;
    }

    return trimmed;
};

const hexToRgbTuple = (hex: string): string | null => {
    const normalized = hex.startsWith('#') ? hex : `#${hex}`;

    if (!HEX_COLOR_PATTERN.test(normalized)) {
        return null;
    }

    return [
        parseInt(normalized.slice(1, 3), 16),
        parseInt(normalized.slice(3, 5), 16),
        parseInt(normalized.slice(5, 7), 16),
    ].join(' ');
};

const formatRadiusPx = (value: number | null | undefined): string | null => {
    if (
        typeof value !== 'number' ||
        !Number.isFinite(value) ||
        !Number.isInteger(value) ||
        value < 0 ||
        value > 9999
    ) {
        return null;
    }

    return `${value}px`;
};

const formatFontWeight = (value: number | null | undefined): string | null => {
    if (
        typeof value !== 'number' ||
        !Number.isFinite(value) ||
        !Number.isInteger(value) ||
        value < 1 ||
        value > 1000
    ) {
        return null;
    }

    return `${value}`;
};

const formatLeadingPercent = (value: number | null | undefined): string | null => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 50 || value > 300) {
        return null;
    }

    return `${value}%`;
};

const formatTrackingPercent = (value: number | null | undefined): string | null => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < -100 || value > 100) {
        return null;
    }

    return `${value / 100}em`;
};

const formatAlphaPercent = (value: number | null | undefined): string | null => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 100) {
        return null;
    }

    return `${value}%`;
};

export const buildWidgetThemeVars = (appearance: AppearanceConfig): WidgetThemeVars => {
    const themeVars: WidgetThemeVars = {};
    const accent = appearance.brand_color;

    if (isHexColor(accent)) {
        const accentRgb = hexToRgbTuple(accent);

        // Malformed values are ignored defensively so we do not emit invalid CSS vars like
        // `--rw-accent-rgb: 240 0 NaN`.
        if (accentRgb) {
            themeVars['--rw-accent'] = accent;
            themeVars['--rw-accent-rgb'] = accentRgb;
            themeVars['--rw-on-accent'] = getContrastingTextColor(accent);
        }
    }

    // Glass-effect alpha stops for the compact shell gradient.
    const glassEffectStartAlpha = formatAlphaPercent(
        appearance.widget_theme?.glass_effect_start_alpha_pct
    );
    if (glassEffectStartAlpha) {
        themeVars['--rw-glass-effect-alpha-start'] = glassEffectStartAlpha;
    }

    const glassEffectEndAlpha = formatAlphaPercent(
        appearance.widget_theme?.glass_effect_end_alpha_pct
    );
    if (glassEffectEndAlpha) {
        themeVars['--rw-glass-effect-alpha-end'] = glassEffectEndAlpha;
    }

    // Shared text and icon roles used across the widget shell.
    const textPrimary = appearance.widget_theme?.text_primary;
    if (isHexColor(textPrimary)) {
        themeVars['--rw-text-primary'] = textPrimary;
    }

    const textSecondary = appearance.widget_theme?.text_secondary;
    if (isHexColor(textSecondary)) {
        themeVars['--rw-text-secondary'] = textSecondary;
    }

    const textPlaceholder = appearance.widget_theme?.text_placeholder;
    if (isHexColor(textPlaceholder)) {
        themeVars['--rw-text-placeholder'] = textPlaceholder;
    }

    const textDisclaimer = appearance.widget_theme?.text_disclaimer;
    if (isHexColor(textDisclaimer)) {
        themeVars['--rw-text-disclaimer'] = textDisclaimer;
    }

    const iconMuted = appearance.widget_theme?.icon_muted;
    if (isHexColor(iconMuted)) {
        themeVars['--rw-icon-muted'] = iconMuted;
    }

    // Font family overrides: shared widget font plus CTA-only font family.
    const fontFamily = normalizePreferredFontFamily(appearance.widget_theme?.font_family);
    if (fontFamily) {
        themeVars['--rw-font-family'] = `${fontFamily}, ${DEFAULT_FONT_FAMILY_FALLBACK}`;
    }

    const ctaFontFamily = normalizePreferredFontFamily(appearance.widget_theme?.cta_font_family);
    if (ctaFontFamily) {
        themeVars['--rw-font-family-cta'] = `${ctaFontFamily}, ${DEFAULT_FONT_FAMILY_FALLBACK}`;
    }

    // Letter spacing and line height for body copy and conversion CTA labels.
    const bodyTracking = formatTrackingPercent(appearance.widget_theme?.body_tracking_pct);
    if (bodyTracking) {
        themeVars['--rw-letter-spacing-body'] = bodyTracking;
    }

    const bodyLeading = formatLeadingPercent(appearance.widget_theme?.body_leading_pct);
    if (bodyLeading) {
        themeVars['--rw-line-height-body'] = bodyLeading;
    }

    const ctaTracking = formatTrackingPercent(appearance.widget_theme?.cta_tracking_pct);
    if (ctaTracking) {
        themeVars['--rw-letter-spacing-cta'] = ctaTracking;
    }

    const ctaLeading = formatLeadingPercent(appearance.widget_theme?.cta_leading_pct);
    if (ctaLeading) {
        themeVars['--rw-line-height-cta'] = ctaLeading;
    }

    // Font weights for shared body/input roles plus CTA-specific emphasis.
    const bodyFontWeight = formatFontWeight(appearance.widget_theme?.body_font_weight);
    if (bodyFontWeight) {
        themeVars['--rw-font-weight-regular'] = bodyFontWeight;
    }

    const bodyStrongFontWeight = formatFontWeight(appearance.widget_theme?.body_strong_font_weight);
    if (bodyStrongFontWeight) {
        themeVars['--rw-font-weight-body-strong'] = bodyStrongFontWeight;
    }

    const inputFontWeight = formatFontWeight(appearance.widget_theme?.input_font_weight);
    if (inputFontWeight) {
        themeVars['--rw-font-weight-input'] = inputFontWeight;
    }

    const placeholderFontWeight = formatFontWeight(
        appearance.widget_theme?.placeholder_font_weight
    );
    if (placeholderFontWeight) {
        themeVars['--rw-font-weight-placeholder'] = placeholderFontWeight;
    }

    const primaryCtaFontWeight = formatFontWeight(appearance.widget_theme?.primary_cta_font_weight);
    if (primaryCtaFontWeight) {
        themeVars['--rw-font-weight-cta-primary'] = primaryCtaFontWeight;
    }

    const secondaryCtaFontWeight = formatFontWeight(
        appearance.widget_theme?.secondary_cta_font_weight
    );
    if (secondaryCtaFontWeight) {
        themeVars['--rw-font-weight-cta-secondary'] = secondaryCtaFontWeight;
    }

    // Radius overrides for compact shells/controls, cards, and CTA buttons.
    // Dynamic-question pills intentionally keep the shared CSS-owned pill radius.
    const compactShellRadius = formatRadiusPx(appearance.widget_theme?.compact_shell_radius);
    if (compactShellRadius) {
        themeVars['--rw-radius-shell-compact'] = compactShellRadius;
    }

    const compactControlRadius = formatRadiusPx(appearance.widget_theme?.compact_control_radius);
    if (compactControlRadius) {
        themeVars['--rw-radius-control-compact'] = compactControlRadius;
    }

    const cardRadius = formatRadiusPx(appearance.widget_theme?.card_radius);
    if (cardRadius) {
        themeVars['--rw-radius-card'] = cardRadius;
    }

    const ctaRadius = formatRadiusPx(appearance.widget_theme?.cta_radius);
    if (ctaRadius) {
        themeVars['--rw-radius-cta'] = ctaRadius;
    }

    return themeVars;
};
